import { createSignal } from '@pexip/signal';
import { isMedia } from './typeGuard';
import { formatUserMediaStatus } from './types';
/**
 * Create a general signal with consistent scoped name
 *
 * @param name - Signal name
 * @param crucial - Signify if the signal is unmissable. @defaultValue true
 * @param variant - The variant of the signal @see Signal @defaultValue 'generic'
 */
export const createMediaSignal = (name, crucial = true, 
// FIXME: need `schedule` function to support `batched` variant
variant = 'generic') => createSignal({
    name: `media/${name}`,
    allowEmittingWithoutObserver: !crucial,
    variant,
});
/**
 * Keeps acquisition and mute state in signal logs without traversing device or
 * processor graphs or running their serialization hooks.
 *
 * @example
 * createSignal({describeSubject: summarizeMedia});
 */
const summarizeMedia = (subject) => {
    if (subject === undefined) {
        return undefined;
    }
    if (!isMedia(subject)) {
        return '[Unparsed media]';
    }
    return {
        id: subject.id,
        active: subject.active,
        status: formatUserMediaStatus(subject.status),
        audioMuted: subject.audioMuted,
        videoMuted: subject.videoMuted,
    };
};
export const REQUIRED_SIGNAL_KEYS = [
    'onMediaChanged',
    'onVAD',
    'onSilentDetected',
];
/**
 * Create and return all required and optional (if specified with `more`),
 * signals for media to work
 *
 * @param more - Keys from `MediaSignalsOptional`, @see MediaSignalsOptional
 * @param scope - any scope prefix for the generated signal name, @see Signal
 *
 * The following signals created by default
 * - 'onMediaChanged',
 * - 'onVAD',
 *
 * @see REQUIRED_SIGNAL_KEYS
 */
export const createMediaSignals = (more, scope = '') => {
    const signalScope = scope && [scope, ':'].join('');
    return [...REQUIRED_SIGNAL_KEYS, ...more].reduce((signals, key) => {
        if (key === 'onMediaChanged') {
            signals[key] = createSignal({
                name: `media/${signalScope}${key}`,
                describeSubject: summarizeMedia,
            });
            return signals;
        }
        // @ts-expect-error
        signals[key] = createMediaSignal(`${signalScope}${key}`);
        return signals;
    }, {});
};
/**
 * Internal signals
 * @internal
 */
export const internalSignals = {
    onDevicesChanged: createSignal({
        name: 'media/devicesChanged',
    }),
};
//# sourceMappingURL=signals.js.map